import { Injectable } from '@angular/core';
import { environment } from '@g99-product-web-ui/shared-environment';
import { Observable, from, of } from 'rxjs';

import { IWebSocketMessage } from './web-socket.interface';
import { WebSocketService } from './web-socket.service';

/**
 * Owns the single shared STOMP connection to g99-central-ws-service.
 *
 * All realtime consumers (inbox messages on /inbox/{businessId}, in-app
 * notifications on /user/{userId}/queue/messages) subscribe topics through
 * this service so the app maintains exactly one central connection.
 * Reconnection and subscription replay are handled by WebSocketService.
 */
@Injectable({
  providedIn: 'root',
})
export class CentralSocketService {
  public static readonly CONNECTION_ID = 'central-ws';

  private _connectPromise: Promise<void> | null = null;

  constructor(private readonly _webSocketService: WebSocketService) {}

  /**
   * Central socket is active whenever an endpoint URL is configured for
   * this environment.
   */
  public isEnabled(): boolean {
    return !!environment.local.apiUrls.centralWsUrl;
  }

  /**
   * Idempotent connect: concurrent callers share one in-flight attempt.
   * Emits immediately when the central socket is disabled.
   */
  public connect(): Observable<void> {
    if (!this.isEnabled()) {
      return of(void 0);
    }

    if (!this._connectPromise) {
      this._connectPromise = this._webSocketService
        .connect(CentralSocketService.CONNECTION_ID, {
          url: environment.local.apiUrls.centralWsUrl,
          reconnectDelay: 5000,
          heartbeatIncoming: 4000,
          heartbeatOutgoing: 4000,
          debug: false,
        })
        .catch((error) => {
          // Allow later retries after a hard failure
          this._connectPromise = null;
          throw error;
        });
    }

    return from(this._connectPromise);
  }

  public subscribeTopic<T>(
    destination: string,
    callback: (message: IWebSocketMessage<T>) => void
  ): string {
    return this._webSocketService.subscribe<T>(
      CentralSocketService.CONNECTION_ID,
      destination,
      callback
    );
  }

  public unsubscribeTopic(subscriptionId: string): void {
    this._webSocketService.unsubscribe(
      CentralSocketService.CONNECTION_ID,
      subscriptionId
    );
  }

  public isConnected(): boolean {
    return this._webSocketService.isConnected(
      CentralSocketService.CONNECTION_ID
    );
  }
}
